'use client';

import React, { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle, RefreshCw, UserCheck, Edit3 } from 'lucide-react';
import { auditService, type DbAuditEntry } from '@/lib/supabase/tprmService';

const ACTION_ICONS: Record<string, React.ElementType> = {
  'Finding Added': ShieldCheck,
  'Issue Opened': AlertTriangle,
  'Review Assigned': UserCheck,
  'Contract Renewal': RefreshCw,
  'Issue Escalated': AlertTriangle,
  'DD Approved': ShieldCheck,
  'Stage Advanced': ShieldCheck,
  'Stage Rejected': AlertTriangle,
  'Stage Returned': RefreshCw,
  'Status Advanced': UserCheck,
};

const ACTION_COLORS: Record<string, string> = {
  'Finding Added': 'text-orange-600 bg-orange-50 dark:bg-orange-950/30',
  'Issue Opened': 'text-red-600 bg-red-50 dark:bg-red-950/30',
  'Review Assigned': 'text-blue-600 bg-blue-50 dark:bg-blue-950/30',
  'Contract Renewal': 'text-amber-600 bg-amber-50 dark:bg-amber-950/30',
  'Issue Escalated': 'text-red-600 bg-red-50 dark:bg-red-950/30',
  'DD Approved': 'text-teal-600 bg-teal-50 dark:bg-teal-950/30',
  'Stage Advanced': 'text-teal-600 bg-teal-50 dark:bg-teal-950/30',
  'Stage Rejected': 'text-red-600 bg-red-50 dark:bg-red-950/30',
  'Stage Returned': 'text-amber-600 bg-amber-50 dark:bg-amber-950/30',
  'Status Advanced': 'text-blue-600 bg-blue-50 dark:bg-blue-950/30',
};

export default function DashboardActivityFeed() {
  const [entries, setEntries] = useState<DbAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    auditService.getRecent(10).then((data) => {
      if (!cancelled) {
        setEntries(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    // Real-time subscription for new audit entries
    const unsubscribe = auditService.subscribeToRecent((updated) => {
      if (!cancelled) setEntries(updated.slice(0, 10));
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
        <div>
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Recent Activity</h2>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
            {loading ? 'Loading…' : `${entries.length} recent entries · All users`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))]">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Live
          </span>
          <button className="btn-ghost text-xs">Full Audit Trail</button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2">
          <RefreshCw size={16} className="animate-spin text-[hsl(var(--accent))]" />
          <span className="text-xs text-[hsl(var(--text-muted))]">Loading activity…</span>
        </div>
      ) : (
        <div className="divide-y divide-[hsl(var(--border))]">
          {entries.map((item) => {
            const ActionIcon = ACTION_ICONS[item.action] || Edit3;
            const colorClass = ACTION_COLORS[item.action] || 'text-zinc-600 bg-zinc-50';
            const timestamp = new Date(item.created_at).toISOString().replace('T', ' ').slice(0, 16);
            return (
              <div key={item.id} className="flex items-start gap-3 px-4 py-3 hover:bg-[hsl(var(--background))] transition-colors">
                <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${colorClass}`}>
                  <ActionIcon size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-[hsl(var(--text-primary))]">{item.actor}</span>
                    <span className="text-2xs text-[hsl(var(--text-muted))] bg-[hsl(var(--background))] px-1.5 py-0.5 rounded">{item.actor_role}</span>
                    <span className="text-2xs font-medium text-[hsl(var(--text-secondary))]">{item.action}</span>
                  </div>
                  <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5 leading-snug">{item.detail}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="text-2xs text-[hsl(var(--text-muted))] font-mono whitespace-nowrap">{timestamp}</span>
                  <p className="text-2xs text-[hsl(var(--text-muted))]">{item.entity_id}</p>
                </div>
              </div>
            );
          })}
          {entries.length === 0 && (
            <div className="py-8 text-center text-xs text-[hsl(var(--text-muted))]">No activity recorded yet.</div>
          )}
        </div>
      )}
    </div>
  );
}